import React, { useEffect, useRef, useState } from "react";
import { resolveInitialPayerPhone } from "../utils/deliveryPhone";
import { useHistory } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { TextField } from "@material-ui/core";
import { makeStyles } from "@material-ui/core/styles";
import { useFormik } from "formik";
import * as Yup from "yup";
import styled from "styled-components";
import CircularProgress from "@material-ui/core/CircularProgress";
import LocationOnIcon from "@mui/icons-material/LocationOn";
// The restaurant placeholder Izi picked: fork, plate and knife on a plain
// near-white field. Used wherever a dish has no photograph yet.
import menuPlaceholder from "../assets/img/restaurant-dish-placeholder.png";
import useDeliveryFee from "../redux/useDeliveryFee";
import {
  createOrder,
  payAirtelMoneyCheckout,
  payCardCheckout,
  payMoMoCheckout,
} from "../redux/restaurantOrderActions";
import CreditCardForm from "../components/CreditCardForm/CreditCardForm";
import airtel from "../assets/img/airtel_money.png";
import momo from "../assets/img/momo.jpg";
import { clearCart } from "../redux/restaurantCartSlice";
import RestaurantNavBar from "../components/RestaurantNavBar";
import CheckoutStepper from "../components/CheckoutStepper";
import DeliveryFeeStatus, { QuotedAmount } from "../components/DeliveryFeeStatus";
import {
  formatCurrency,
  isFwMomoMethod,
  fwNetworkFor,
} from "../config/market";
import useMinimumOrderValue from "../hooks/useMinimumOrderValue";
import { linesSubtotal, cartMinimumVerdict } from "../utils/minimumOrder";
import { cartOutletId } from "../utils/vendorOutlets";
import { newIdempotencyKey } from "../utils/idempotency";
import { extractCreatedOrderId, orderChargeAmount, ORDER_TOTAL_UNREADABLE } from "../redux/checkout";
import { followCardRedirect } from "../utils/cardPayment";
import { apiSetContactEmail } from "../api/profileApi";
import useRestaurantAvailability from "../hooks/useRestaurantAvailability";
import RestaurantAvailabilityBanner from "../components/RestaurantAvailabilityBanner";
import { availabilityAllowsOrdering } from "../utils/restaurantAvailability";
import useCheckoutDeliverySchedule from "../hooks/useCheckoutDeliverySchedule";
import { useCartBranchCheck } from "../hooks/useBranchCartCheck";
import BranchCartReviewDialog from "../components/branch/BranchCartReviewDialog";
import DeliveryTimePicker from "../components/DeliveryTimePicker";
import {
  Heading,
  SubHeading,
  Columns,
  Panel,
  PanelBody,
  PanelTitle,
  DeliverTo,
  Pin,
  DeliverWho,
  DeliverWhere,
  PayChip,
  ItemRow,
  Thumb,
  ItemInfo,
  ItemName,
  ItemMeta,
  ItemSum,
  Summary,
  CostRow,
  Rule,
  TotalRow,
  PayLine,
  FieldRow,
  OptionalTag,
  EmailHint,
  Cta,
  AirtelCta,
  PayLogo,
  Reassure,
} from "../components/Layout/confirmLayout";
import { toast } from "react-toastify";
import { StruckPrice, VendorOfferAmount } from "../components/PriceCut";
import { lineHasDeal, cartOfferSavings } from "../utils/vendorDiscounts";
import { goToSignIn } from "../utils/signInReturn";

// ─────────────────────────────────────────────────────────────────────────────
// Rams-optimized Confirm page (per Lusuku_Confirm_Redesign_Mockup.html):
// narrow centered column instead of a full-viewport table; "Deliver to" at the
// top; borderless item rows; sticky cost summary where the TOTAL is the
// loudest element. PRESENTATION ONLY — every handler, fee gate
// (deliveryFeeReady), DeliveryFeeStatus usage and disabled condition is
// unchanged (Order-Placement Contract §B/§C).
// ─────────────────────────────────────────────────────────────────────────────

const useStyles = makeStyles(() => ({
  input: {
    "& input[type=number]": {
      "-moz-appearance": "textfield",
    },
    "& input[type=number]::-webkit-outer-spin-button": {
      "-webkit-appearance": "none",
      margin: 0,
      paddingBottom: "5px",
    },
    "& input[type=number]::-webkit-inner-spin-button": {
      "-webkit-appearance": "none",
      margin: 0,
    },
  },
  inputGreen: {
    "& .MuiOutlinedInput-root": {
      "& fieldset": {
        borderColor: "green",
      },
    },
    "&:hover .MuiOutlinedInput-root .MuiOutlinedInput-notchedOutline": {
      borderColor: "green",
    },
    "& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline": {
      borderColor: "green",
    },
    "& .MuiOutlinedInput-input": {
      color: "green",
    },
    "&:hover .MuiOutlinedInput-input": {
      color: "green",
    },
    "& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-input": {
      color: "green",
    },
  },
}));

const StyledTextField = styled(TextField)`
  & .MuiInputBase-root {
    padding: 10px; /* Add padding to ensure proper alignment */
  }
  & .MuiOutlinedInput-input {
    padding: 12px; /* Adjust padding to align placeholder */
  }
`;

const validationSchema = Yup.object().shape({
  cardNumber: Yup.string().required("Card Number is required."),
  expiry_month: Yup.string().required("Expiry Month is required."),
  expiry_year: Yup.string().required("Expiry Year is required."),
  cardCvv: Yup.string().required("CVV is required."),
  cardType: Yup.string().required("Card Type is required."),
  fullName: Yup.string().required("Full Name is required."),
});

const RestaurantPlaceOrderScreen = () => {
  const dispatch = useDispatch();
  const history = useHistory();
  const classes = useStyles();

  const { shippingAddress, paymentMethod, total } = useSelector(
    (state) => state.restaurantCart
  );
  const currentRestaurant = useSelector(
    (state) => state.restaurantCart.currentRestaurant
  );

  const cart = useSelector(
    (state) => state.restaurantCart.carts[currentRestaurant] || {}
  );

  const products = Object.values(cart);
  // What the vendor's live offers took off; the subtotal is already net of it.
  const offerSavings = cartOfferSavings(products);

  /* Second line of defence on the minimum order value. The cart says it first
     and the server is the gate that counts; this stops a deep link or a tab
     left open since before the floor existed from posting a basket the server
     will only refuse after the customer has entered a phone number. */
  const minimumOrderValue = useMinimumOrderValue();
  // One branch per restaurant cart (4 Oct 2026), so the first line naming
  // one names the cart's.
  const outletId = cartOutletId(products);

  /* ONE vendor id for the fee quote AND the order it pays for. The server's
     quote-token check (quoteService, `vendorMatches`) is a strict string
     compare of the signed payload.vendorId against the vendorId it resolves
     for the order. The quote used to be fetched for `products[0].restaurantId`
     — the Restaurants._id stamped on every menu document — while the order was
     created with `currentRestaurant`, the cart key, which is the OWNER id
     whenever the shopper arrived by an owner-id link (SectionComponent keys
     the cart on `restaurant.owner` outright). Those two ids are different
     documents, so the compare failed, the signed token was thrown away and the
     fee was silently recomputed: the customer paid a delivery fee that was
     never on screen. Derive it once and the two cannot drift apart. */
  const orderVendorId =
    currentRestaurant ||
    products?.[0]?.restaurantId ||
    products?.[0]?.vendorId ||
    null;

  const availability = useRestaurantAvailability({
    vendorId: currentRestaurant,
    outletId,
  });

  const user = useSelector((state) => state.user.currentUser);
  const userId = user?._id || user?.result?._id;
  const userName = user?.name || user?.businessName || user?.result?.name || user?.result?.businessName;
  const userEmail = user?.email || user?.result?.email;
  // Optional email capture. Most Lusuku customers register by PHONE and have no
  // email, so the receipt has nowhere to go. We ask here; never required.
  const [emailInput, setEmailInput] = useState("");
  const needsEmail = !userEmail;

  const { orderCreationStatus, paymentCreationStatus } = useSelector(
    (state) => state.restaurantOrder
  );

  const [orderSource] = useState("Web");

  /* An order placed from this screen empties the cart (every rail now clears
     it the moment the order is persisted), and an empty cart used to trip the
     bounce below in the very same commit as the success redirect — pushing the
     landing page underneath the confirmation screen, and on the card/Airtel
     rails racing the off-site redirect itself. The ref is set BEFORE the cart
     is cleared, so the guard is already disarmed when this re-runs. */
  const orderPlacedRef = useRef(false);

  useEffect(() => {
    if (orderPlacedRef.current) return;
    if (
      !shippingAddress || Object.keys(shippingAddress).length === 0 ||
      !paymentMethod ||
      !products || products.length === 0
    ) {
      history.push(`/`);
    }
  }, [shippingAddress, paymentMethod, products, history, currentRestaurant]);

  // Server-authoritative delivery fee (distance-based, signed quote).
  const {
    fee: serverShippingFee,
    offer: deliveryOffer,
    distanceKm,
    quoteToken,
    isReady: deliveryFeeReady,
    isFailed: deliveryFeeFailed,
    isBlocked: deliveryFeeBlocked,
    error: deliveryFeeError,
    refresh: refreshDeliveryFee,
    itemsSubtotal: quotedItemsSubtotal,
    total: quotedTotal,
    minimumOrder: quotedMinimumOrder,
    goodsPriced: quotedGoodsPriced,
    paymentProcessing,
  } = useDeliveryFee({
    vendorId: orderVendorId,
    /* An outlet has its own coordinates and its own delivery radius. Order
       creation already prices from the outlet — `resolveOrderFeeFields` reads
       the outletId off the order lines — but the QUOTE this screen displays
       did not send one, so /api/quote fell back to the parent restaurant. The
       shopper was shown the parent's distance and fee and then charged the
       outlet's, and the distance mismatch discarded the signed token on the
       way through. Both sides now measure from the same address. */
    outletId,
    customerLat: shippingAddress?.latLng?.lat,
    customerLng: shippingAddress?.latLng?.lng,
    items: products,
    // The checkout's payment method: the server's total then carries the
    // processing fee order create adds for it.
    paymentMethod,
  });
  /* Second line of defence on the minimum order value: the SERVER's verdict
     for the quoted lines (minimumOrder on POST /api/quote), the cart's own
     sum only until that answer arrives. The server is the gate at order create. */
  const belowMinimumOrder =
    cartMinimumVerdict(
      { goodsPriced: quotedGoodsPriced, itemsSubtotal: quotedItemsSubtotal, minimumOrder: quotedMinimumOrder },
      { subtotal: linesSubtotal(products), minimum: minimumOrderValue }
    ).shortfall > 0;
  // Sent as the order body's totalPrice, which order create recomputes. The
  // screen shows the server's total (quotedTotal), and every rail charges the
  // created order's own totalPrice (orderChargeAmount below).
  const totalOrderCostPlusShipping = Number(total) + Number(serverShippingFee);

  // Express or a scheduled window, from the same restaurant (and outlet) whose
  // hours the open-status check reads. Closed-now restaurants that take
  // scheduled orders force Scheduled.
  const deliveryTime = useCheckoutDeliverySchedule({
    flow: "restaurant",
    vendorId: currentRestaurant,
    outletId,
    scheduledOnly: availability.scheduledOrdersOnly,
  });

  /* The cart check at its branch (4 Oct 2026). On this page's load the cart
     is checked (a cart read back from localStorage, or moved, may hold a line
     the branch no longer sells at that price), and every create-order call
     below goes through guardSubmit: checked first when a line is
     unconfirmed, and a 409 BRANCH_LINES_CHANGED opens "Changes at <branch>"
     from the server's own lines. A failed check never blocks the order. */
  const branchCheck = useCartBranchCheck({
    vertical: "restaurant",
    cartId: currentRestaurant,
    checkWhen: Boolean(currentRestaurant),
  });
  const submitOrder = (thunk, payload) =>
    branchCheck.guardSubmit(() => deliveryTime.submitOrder(thunk, payload));

  // One idempotency key per checkout attempt: retries of THIS order reuse it so
  // the backend dedupes instead of double-creating (Contract §B4).
  const idempotencyKeyRef = useRef(newIdempotencyKey());

  // Shared props for the fee/total cells: fee failure must be VISIBLE with a
  // Retry, never a permanent "Calculating…" (Contract §C3).
  const feeStatusProps = {
    isReady: deliveryFeeReady,
    isFailed: deliveryFeeFailed,
    isBlocked: deliveryFeeBlocked,
    error: deliveryFeeError,
    onRetry: refreshDeliveryFee,
  };

  useEffect(() => {
    // REPLACE (not push) so this confirm screen falls out of the back stack —
    // Back from the success page must never land on a re-submittable
    // checkout step (double-order protection; pairs with the success page's
    // useBlockBackNavigation guard).
    if (paymentMethod === "MOMO" && !isFwMomoMethod("MOMO") && orderCreationStatus === "fulfilled") {
      // Direct-MTN status page is Uganda-only; FW-momo markets redirect to Flutterwave.
      history.replace("/momoOrderPage");
    } else if (paymentMethod === "COD" && orderCreationStatus === "fulfilled") {
      history.replace("/orderPage");
    }
  }, [orderCreationStatus, paymentMethod, history]);

  const formik = useFormik({
    initialValues: {
      cardNumber: "",
      expiry_month: "",
      expiry_year: "",
      cardCvv: "",
      cardType: "",
      fullName: "",
    },
    validationSchema,
    onSubmit: (values) => {
      cardPlaceOrderHandler(values);
    },
  });

  const [postData, setPostData] = useState({
    mobile: "",
  });  
  // Payer prefill: most customers pay with the number we deliver to (or their
  // account number), so seed the mobile-money field once instead of forcing a
  // retype. Fully editable for "someone else is paying", and the once-guard
  // means we never fight a customer who clears the field on purpose.
  const payerPrefilledRef = useRef(false);
  useEffect(() => {
    if (payerPrefilledRef.current) return;
    const prefill = resolveInitialPayerPhone(shippingAddress, user);
    if (prefill) {
      payerPrefilledRef.current = true;
      setPostData((d) => (d.mobile ? d : { ...d, mobile: prefill }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [shippingAddress, user]);


  useEffect(() => {
    if (!userId) {
      goToSignIn(history);
    }
  }, [userId, history]);

  const confirmRestaurantAvailable = async () => {
    const latest = await availability.refresh();
    return availabilityAllowsOrdering(latest);
  };

  const placeOrderHandler = async (e) => {
    e.preventDefault();
    if (!deliveryFeeReady || !(await confirmRestaurantAvailable())) return;
    submitOrder(createOrder, {
      orderItems: products?.map((item) => ({
        ...item,
        vendorId: item.vendorId,
        outletId: outletId || undefined,
      })),
      shippingAddress,
      // Server recomputes the fee/total; quote token lets it honour the
      // displayed fee. No client-computed fee is sent as authoritative.
      clientQuoteToken: quoteToken,
      distanceKm,
      paymentMethod,
      itemsPrice: total,
      totalPrice: totalOrderCostPlusShipping,
      customerName: userName,
      client_id: userId,
      orderSrc: orderSource,
      vendorId: orderVendorId,
      qty: products?.map((item) => item.quantity),
      image: products?.map((item) => item.image),
      idempotencyKey: idempotencyKeyRef.current,
    }).then((action) => {
      // Only clear the cart once the order is actually persisted. On a rejected
      // order (e.g. 422 OUT_OF_DELIVERY_RANGE) keep the cart so the customer
      // can fix their address and retry (Contract §C5).
      if (!action.error && action.meta?.requestStatus === "fulfilled") {
        orderPlacedRef.current = true;
        dispatch(clearCart(currentRestaurant));
      }
    });
  };

  // Canonical restaurant order items for the create-order + payment payloads.
  // vendorId falls back item.restaurantId → currentRestaurant so the backend
  // can always attribute the vendor (items added via some flows lack restaurantId).
  const buildOrderItems = () =>
    products.map((item) => ({
      _id: item._id,
      name: item.name,
      vendorId: item.vendorId || item.restaurantId || currentRestaurant,
      restaurantId: item.restaurantId,
      // The cart's ONE branch (4 Oct 2026): the same id the fee quote, the
      // hours check and the delivery slots above read, on every line.
      outletId: outletId || undefined,
      qty: item.quantity,
      price: item.price,
      image: item.image,
      /* THE OPTIONS. Leaving these out did not merely lose the "extra
         chicken" line from the receipt — it broke paid ordering outright.

         The cart total INCLUDES option prices (`calculateTotal` in
         restaurantCartSlice sums `item.options[].price` into every line), and
         that is the figure the customer is shown and the figure the client
         asks the payment rail to charge. The server recomputes the order
         total from what it receives, and `getOrderLineGross` also sums
         `item.options[].price` — but options were never in the payload, so
         its total came back SHORT by exactly the option money. Then
         `verifyChargeAmount` compares the two, and it blocks by default
         (`PAYMENT_AMOUNT_ENFORCED` defaults to "true"), answering 400
         PAYMENT_AMOUNT_MISMATCH.

         So: any dish with one paid option, on any prepaid rail, created the
         order and then had its own charge refused. Sending the options makes
         both sides compute the same number, which is the only reason the
         guard was firing. */
      options: Array.isArray(item.options) ? item.options : [],
    }));

  // Shared create-order payload (server recomputes the fee/total; the quote
  // token lets it honour the displayed fee — Contract §B1/§B2).
  const buildCreateOrderPayload = (orderItems) => ({
    orderItems,
    shippingAddress,
    clientQuoteToken: quoteToken,
    distanceKm,
    paymentMethod,
    itemsPrice: total,
    totalPrice: totalOrderCostPlusShipping,
    customerName: userName,
    client_id: userId,
    orderSrc: orderSource,
    vendorId: orderVendorId,
    idempotencyKey: idempotencyKeyRef.current,
  });

  /* The order is persisted by the time this runs, so from here the customer
     is paying THAT order. Leaving the cart full let them walk back from
     Flutterwave — or from the momo status page — into a checkout that would
     happily create a SECOND order for the same food and charge for it again.
     COD has always cleared here; the three prepaid rails never did.

     Clearing on order-CREATED rather than on payment-confirmed is deliberate.
     A failed or abandoned charge does not make the order disappear, and the
     right recovery is to pay the order that exists (it is in My Profile, with
     its own retry), not to rebuild a cart beside it and place a duplicate. */
  const markOrderPlaced = () => {
    orderPlacedRef.current = true;
    dispatch(clearCart(currentRestaurant));
  };

  const payMoMoCheckoutHandler = async (e) => {
    e.preventDefault();
    // MARKET RAIL SWITCH: ZM "MOMO" charges over the FW rail (network MTN);
    // Uganda keeps the direct MTN rail below.
    if (isFwMomoMethod("MOMO")) {
      return payAirtelMoneyCheckoutHandler(e, fwNetworkFor("MOMO"));
    }
    // Never charge before the server fee is known (Contract §C1) — otherwise
    // the customer is charged items-only and the shipping fee is lost revenue.
    if (!deliveryFeeReady || !(await confirmRestaurantAvailable())) return;

    // WHY save but not send: no MoMo charge payload in this codebase carries an
    // email, so we only persist it to the profile — that is where the order
    // confirmation is addressed from. Best-effort; it must never block the pay.
    const typedEmail = emailInput.trim();
    if (!userEmail && typedEmail) {
      apiSetContactEmail(typedEmail).catch(() => {});
    }
    const orderItems = buildOrderItems();

    // Create the order FIRST and await it (Contract §B3). Previously payment
    // and createOrder fired concurrently, so a customer could be charged with
    // no order — or the payment bound to their "latest" unrelated order.
    const orderAction = await submitOrder(createOrder, buildCreateOrderPayload(orderItems));
    if (orderAction.error || orderAction.meta?.requestStatus !== "fulfilled") {
      return; // thunk already surfaced the backend message; do NOT charge
    }
    const orderId = extractCreatedOrderId(orderAction);
    // THE amount this order is charged: its server totalPrice, as order
    // create returned it (redux/checkout orderChargeAmount), never a sum
    // made on this screen.
    const chargeAmount = orderChargeAmount(orderAction);
    if (chargeAmount === null) {
      toast.error(ORDER_TOTAL_UNREADABLE, { autoClose: false });
      return;
    }
    markOrderPlaced();

    dispatch(
      payMoMoCheckout({
        mobile: postData.mobile,
        totalPrice: chargeAmount,
        client_id: userId,
        orderItems,
        // Bind the payment to the order we just created (Contract §B3),
        // instead of the backend's legacy latest-order fallback.
        orderId,
      })
    );
  };

  const generateTxRef = (clientId) => {
    return clientId + "-" + Date.now();
  };

  const payAirtelMoneyCheckoutHandler = async (e, networkOverride) => {
    e.preventDefault();
    if (!deliveryFeeReady || !(await confirmRestaurantAvailable())) return; // Contract §C1 — no charge without a server fee
    const orderItems = buildOrderItems();

    // Create + await the order before charging (see payMoMoCheckoutHandler).
    const orderAction = await submitOrder(createOrder, buildCreateOrderPayload(orderItems));
    if (orderAction.error || orderAction.meta?.requestStatus !== "fulfilled") {
      return;
    }
    const orderId = extractCreatedOrderId(orderAction);
    // THE amount this order is charged: its server totalPrice, as order
    // create returned it (redux/checkout orderChargeAmount), never a sum
    // made on this screen.
    const chargeAmount = orderChargeAmount(orderAction);
    if (chargeAmount === null) {
      toast.error(ORDER_TOTAL_UNREADABLE, { autoClose: false });
      return;
    }
    markOrderPlaced();
    // Embed the order id in tx_ref as `${clientId}-${orderId}` so the
    // Flutterwave webhook can bind payment to THIS order (Contract §B3).
    const uniqueTxRef = orderId ? `${userId}-${orderId}` : generateTxRef(userId);

    // Use whatever they typed for THIS charge and save it to the profile too.
    // Best-effort: a failed save must never stop the payment.
    const typed = emailInput.trim();
    const effectiveEmail = userEmail || typed || undefined;
    if (!userEmail && typed) {
      apiSetContactEmail(typed).catch(() => {});
    }

    dispatch(
      payAirtelMoneyCheckout({
        mobile: postData.mobile,
        totalPrice: chargeAmount,
        client_id: userId,
        email: effectiveEmail,
        uniqueTxRef,
        network: networkOverride || fwNetworkFor(paymentMethod) || paymentMethod,
        redirect_url: `${window.location.origin}/airtelPay`,
        orderItems,
        orderId,
      })
    ).then((action) => {
      if (!action.error && action.payload?.data?.meta?.authorization) {
        followCardRedirect(action.payload.redirectUrl);
      } else if (!action.error) {
        // The order already EXISTS: createOrder ran and was awaited above, so
        // silence here leaves the customer holding an unpaid order with no next
        // step and no idea anything went wrong. A REJECTED thunk has already
        // toasted the backend's own reason, so re-toasting it would just double
        // up; a 200 whose body carries no `meta.authorization` had no branch at
        // all, and that is the case this else was missing.
        toast.error(
          "We couldn't start the Airtel Money payment. Your order was created but has NOT been paid \u2014 open it in My Orders to try again.",
          { autoClose: false }
        );
      }
    });
  };

  const cardPlaceOrderHandler = async () => {
    if (!deliveryFeeReady || !(await confirmRestaurantAvailable())) return; // Contract §C1 — no charge without a server fee
    const cardDetails = {
      cardNumber: formik.values.cardNumber,
      expiry_month: formik.values.expiry_month,
      expiry_year: formik.values.expiry_year,
      cardCvv: formik.values.cardCvv,
      cardType: formik.values.cardType,
      fullName: formik.values.fullName,
    };

    // Create + await the order before charging (see payMoMoCheckoutHandler).
    const orderAction = await submitOrder(createOrder, buildCreateOrderPayload(buildOrderItems()));
    if (orderAction.error || orderAction.meta?.requestStatus !== "fulfilled") {
      return;
    }
    const orderId = extractCreatedOrderId(orderAction);
    // THE amount this order is charged: its server totalPrice, as order
    // create returned it (redux/checkout orderChargeAmount), never a sum
    // made on this screen.
    const chargeAmount = orderChargeAmount(orderAction);
    if (chargeAmount === null) {
      toast.error(ORDER_TOTAL_UNREADABLE, { autoClose: false });
      return;
    }
    markOrderPlaced();

    // Same optional-email treatment as Airtel (see payAirtelMoneyCheckoutHandler).
    const typed = emailInput.trim();
    const effectiveEmail = userEmail || typed || undefined;
    if (!userEmail && typed) {
      apiSetContactEmail(typed).catch(() => {});
    }

    dispatch(
      payCardCheckout({
        cardDetails,
        totalPrice: chargeAmount,
        client_id: userId,
        customer_name: userName,
        email: effectiveEmail,
        network: paymentMethod,
        redirect_url: `${window.location.origin}/cardOrder`,
        // Bind the card charge to THIS order: the backend builds
        // tx_ref = `${client_id}-${orderId}-${ts}` from body.orderId.
        orderId,
      })
    )
      .then((action) => {
        if (action.payload.redirectUrl) {
          followCardRedirect(action.payload.redirectUrl);
        }
      })
      .catch((err) => {
        console.error("Error while dispatching payCardCheckout:", err);
      });
  };

  // Rendered inside each wired payment block below. Deliberately not `required`,
  // not validated, and absent from every disabled= expression: ask, never block.
  const optionalEmailField = needsEmail ? (
    <FieldRow>
      <StyledTextField
        label={
          <span>
            Email <OptionalTag>optional</OptionalTag>
          </span>
        }
        variant="outlined"
        type="email"
        color="secondary"
        value={emailInput}
        onChange={(ev) => setEmailInput(ev.target.value)}
        fullWidth
        name="contactEmail"
        placeholder="you@example.com"
        autoComplete="email"
      />
      <EmailHint>Add an email and we'll send your receipt there.</EmailHint>
    </FieldRow>
  ) : null;

  let airtelButtonText;
  switch (true) {
    case paymentCreationStatus === "processing payment":
      airtelButtonText = "Processing Payment...";
      break;
    case paymentCreationStatus === "fulfilled":
      airtelButtonText = "Payment Initiated!";
      break;
    case paymentCreationStatus === "rejected":
      airtelButtonText = "Payment attempt Failed, try again!";
      break;
    case orderCreationStatus === "pending":
      airtelButtonText = "Creating Order...";
      break;
    case orderCreationStatus === "fulfilled":
      airtelButtonText = "Order Created...";
      break;
    default:
      airtelButtonText = "Proceed to Pay";
  }

  // Display helpers (presentation only)
  const contactDisplay =
    shippingAddress.customerNumber &&
    Object.keys(shippingAddress.customerNumber).length !== 0
      ? shippingAddress.customerNumber
      : "Not available";
  const zoneDisplay =
    shippingAddress.locationName &&
    Object.keys(shippingAddress.locationName).length !== 0
      ? shippingAddress.locationName.vicinity
      : "Not available";
  const paymentDisplay =
    paymentMethod && Object.keys(paymentMethod).length !== 0
      ? { COD: "Cash on delivery", MOMO: "MTN Mobile Money", Airtel: "Airtel Money", Visa: "Card" }[paymentMethod] || paymentMethod
      : "Not available";
  const itemCount = products.reduce((n, p) => n + (Number(p.quantity) || 0), 0);

  // The phone row used by both MoMo and Airtel (identical logic/constraints).
  const renderMobileInput = (label, placeholder) => (
    <FieldRow>
      <StyledTextField
        className={`${classes.input} ${postData?.mobile?.length === 10 ? classes.inputGreen : ""}`}
        id="outlined-basic"
        label={label}
        variant="outlined"
        type="number"
        color="secondary"
        value={postData?.mobile}
        onChange={(e) => {
          if (e.target.value.length <= 10) {
            setPostData({
              ...postData,
              mobile: e.target.value,
            });
          }
        }}
        inputProps={{
          maxLength: 10,
        }}
        onKeyDown={(e) => {
          if (e.key === "ArrowUp" || e.key === "ArrowDown") {
            e.preventDefault();
          }
        }}
        fullWidth
        name="mobile"
        placeholder={placeholder}
        required
      />
    </FieldRow>
  );

  return (
    <>
      <RestaurantNavBar />
      <BranchCartReviewDialog {...branchCheck.dialogProps} />
      <Page>
        <Wrap>
          <CheckoutStepper currentStep="confirm" />

          <Heading>Review &amp; confirm</Heading>
          <SubHeading>One last look before we place your order.</SubHeading>
          <RestaurantAvailabilityBanner availability={availability} />

          <Columns>
            {/* LEFT — where & what */}
            <div>
              <Panel>
                <DeliverTo>
                  <Pin>
                    <LocationOnIcon style={{ fontSize: 18 }} />
                  </Pin>
                  <div style={{ minWidth: 0 }}>
                    <DeliverWho>Deliver to · {contactDisplay}</DeliverWho>
                    <DeliverWhere>{zoneDisplay}</DeliverWhere>
                  </div>
                  <PayChip>{paymentDisplay}</PayChip>
                </DeliverTo>
                <PanelBody>
                  <PanelTitle>
                    Your order · {itemCount} item{itemCount !== 1 ? "s" : ""}
                  </PanelTitle>
                  {products.map((item) => {
                    const optionsTotal = item.options.reduce(
                      (sum, option) => sum + option.price,
                      0
                    );
                    const itemTotal =
                      (item.price + optionsTotal) * item.quantity;
                    return (
                      <ItemRow key={item?._id}>
                        <Thumb
                          src={item.image || menuPlaceholder}
                          alt={item?.name}
                          onError={(e) => (e.target.src = menuPlaceholder)}
                        />
                        <ItemInfo>
                          <ItemName>{item?.name}</ItemName>
                          <ItemMeta>
                            Qty {item?.quantity} ·{" "}
                            {lineHasDeal(item) && (
                              <StruckPrice>
                                {formatCurrency(item.listPrice + optionsTotal)}
                              </StruckPrice>
                            )}
                            {formatCurrency(item.price + optionsTotal)}
                          </ItemMeta>
                        </ItemInfo>
                        <ItemSum>{formatCurrency(itemTotal)}</ItemSum>
                      </ItemRow>
                    );
                  })}
                </PanelBody>
              </Panel>

              <DeliveryTimePicker controller={deliveryTime} />

              {/* Card details live in the wide column; the pay button stays in
                  the summary so the money story reads top-to-bottom. */}
              {paymentMethod === "Visa" && (
                <Panel style={{ marginTop: 16 }}>
                  <PanelBody>
                    <PanelTitle>Card details</PanelTitle>
                    <CreditCardForm
                      onFormChange={(data) => {
                        let expiry_month, expiry_year;
                        if (typeof data.expirationDate === "string") {
                          [expiry_month, expiry_year] =
                            data.expirationDate.split("/");
                        } else {
                          expiry_month = null;
                          expiry_year = null;
                        }

                        formik.setValues({
                          cardNumber: data.cardNumber,
                          expiry_month,
                          expiry_year,
                          cardCvv: data.cvv,
                          cardType: data.cardType,
                          fullName: data.fullName,
                        });
                      }}
                      values={formik.values}
                      errors={formik.errors}
                      touched={formik.touched}
                      handleBlur={formik.handleBlur}
                      handleChange={formik.handleChange}
                    />
                  </PanelBody>
                </Panel>
              )}
            </div>

            {/* RIGHT — money, sticky, calm; the Total is the loudest element */}
            <Summary>
              <Panel>
                <PanelBody>
                  <CostRow>
                    <span className="k">Subtotal</span>
                    <span className="v"><QuotedAmount {...feeStatusProps} amount={quotedItemsSubtotal} /></span>
                  </CostRow>
                  {offerSavings > 0 && (
                    <CostRow>
                      <span className="k">Vendor offer</span>
                      <VendorOfferAmount className="v">
                        -{formatCurrency(offerSavings)}
                      </VendorOfferAmount>
                    </CostRow>
                  )}
                  <CostRow>
                    <span className="k">Delivery</span>
                    <span className="v" style={{ color: "#03660c", fontWeight: 600 }}>
                      <DeliveryFeeStatus
                        {...feeStatusProps}
                        offer={deliveryOffer}
                        value={formatCurrency(serverShippingFee || 0)}
                      />
                    </span>
                  </CostRow>
                  <Rule />
                  <TotalRow>
                    <span className="k">Total</span>
                    <span className="v">
                      <QuotedAmount
                        {...feeStatusProps}
                        amount={quotedTotal}
                        paymentProcessing={paymentProcessing}
                        failedMessage="We couldn't work out the total."
                      />
                    </span>
                  </TotalRow>
                  <PayLine>
                    Paying by <b>{paymentDisplay}</b>
                  </PayLine>

                  {paymentMethod === "COD" && (
                    <>
                      <Cta
                        onClick={placeOrderHandler}
                        disabled={
                          orderCreationStatus === "pending" ||
                          !deliveryFeeReady ||
                          !availability.canOrder ||
                          belowMinimumOrder
                        }
                      >
                        {orderCreationStatus === "pending" ? (
                          <>
                            <CircularProgress
                              size={18}
                              style={{ marginRight: "8px", color: "#ffffff" }}
                            />
                            Processing...
                          </>
                        ) : (
                          "Place order"
                        )}
                      </Cta>
                      <Reassure>
                        You won't be charged now — you pay the rider on delivery.
                      </Reassure>
                    </>
                  )}

                  {paymentMethod === "MOMO" && (
                    <>
                      {renderMobileInput("Enter MTN Number", "Enter your MTN Number...")}
                      {optionalEmailField}
                      <Cta
                        onClick={payMoMoCheckoutHandler}
                        disabled={
                          postData.mobile.length < 10 ||
                          !deliveryFeeReady ||
                          !availability.canOrder ||
                          belowMinimumOrder ||
                          orderCreationStatus === "pending" ||
                          paymentCreationStatus === "pending" ||
                          orderCreationStatus === "fulfilled"
                        }
                      >
                        <PayLogo src={momo} alt="mtn-logo" />
                        Proceed to Pay
                      </Cta>
                      <Reassure>
                        You'll get a prompt on this number to approve the payment.
                      </Reassure>
                    </>
                  )}

                  {(paymentMethod === "Airtel" || paymentMethod === "ZAMTEL") && (
                    <>
                      {renderMobileInput("Enter Airtel Number", "Enter your Airtel Number...")}
                      {optionalEmailField}
                      <AirtelCta
                        onClick={payAirtelMoneyCheckoutHandler}
                        disabled={
                          postData.mobile.length < 10 ||
                          !deliveryFeeReady ||
                          !availability.canOrder ||
                          belowMinimumOrder ||
                          orderCreationStatus === "pending" ||
                          paymentCreationStatus === "pending" ||
                          paymentCreationStatus === "processing payment" ||
                          paymentCreationStatus === "fulfilled" ||
                          orderCreationStatus === "fulfilled"
                        }
                      >
                        <PayLogo src={airtel} alt="airtel-logo" />
                        {airtelButtonText}
                      </AirtelCta>
                      <Reassure>
                        You'll get a prompt on this number to approve the payment.
                      </Reassure>
                    </>
                  )}

                  {paymentMethod === "Visa" && (
                    <>
                      {optionalEmailField}
                      <Cta
                        onClick={formik.submitForm}
                        disabled={
                          paymentCreationStatus === "pending" ||
                          orderCreationStatus === "pending" ||
                          !deliveryFeeReady ||
                          !availability.canOrder ||
                          belowMinimumOrder ||
                          !formik.isValid
                        }
                      >
                        {paymentCreationStatus === "pending" ||
                        orderCreationStatus === "pending"
                          ? "Processing..."
                          : "Pay"}
                      </Cta>
                      <Reassure>
                        You'll be redirected to complete the card payment securely.
                      </Reassure>
                    </>
                  )}
                </PanelBody>
              </Panel>
            </Summary>
          </Columns>
        </Wrap>
      </Page>
    </>
  );
};

export default RestaurantPlaceOrderScreen;

// ── Styled Components — narrow centered column, flat surfaces, no table chrome ──
const Page = styled.div`
  background: #f4f6f6;
  min-height: 100vh;
`;

const Wrap = styled.div`
  max-width: 920px;
  margin: 0 auto;
  padding: 20px 20px 64px;
  margin-top: 70px;
  padding-top: 60px;

  @media (max-width: 768px) {
    padding-top: 165px; /* room for the mobile vendor banner */
    padding-left: 10px;
    padding-right: 10px;
  }

  @media (max-width: 480px) {
    padding-top: 60px;
    padding-bottom: 20px;
  }
`;
